// Join-request sound: a short chime the host hears when someone knocks. Sounds
// are SYNTHESIZED with the Web Audio API — no bundled audio assets, no external
// requests (§4.2 zero-runtime-dependency / no-CDN). The choice is an ORG-wide
// setting (admin Settings), delivered to the host on the join grant. The
// meeting-open chime is the one bundled asset — still same-origin (no CDN /
// egress), so §4.2 holds.

import ringtoneBrizinaDefaultUrl from './assets/sounds/ringtone-brizina-default.mp3';
import ringtoneQuietAlgiersUrl from './assets/sounds/ringtone-quiet-connection-algiers.mp3';
import ringtoneQuietUrl from './assets/sounds/ringtone-quiet-connection.mp3';
import ringtoneWarmUrl from './assets/sounds/ringtone-warm-connection.mp3';
import meetingStartUrl from './assets/sounds/meeting-start.mp3';

export interface KnockSound {
  id: string;
  label: string;
  /** notes as [frequencyHz, startSeconds, durationSeconds]. */
  notes: [number, number, number][];
  type?: OscillatorType;
}

export const KNOCK_SOUNDS: KnockSound[] = [
  { id: 'ding', label: 'Ding', type: 'sine', notes: [[880, 0, 0.4]] },
  { id: 'chime', label: 'Chime', type: 'sine', notes: [[659, 0, 0.16], [988, 0.13, 0.45]] },
  {
    id: 'marimba',
    label: 'Marimba',
    type: 'triangle',
    notes: [[523, 0, 0.16], [659, 0.11, 0.16], [784, 0.22, 0.34]],
  },
  { id: 'knock', label: 'Knock', type: 'triangle', notes: [[190, 0, 0.09], [190, 0.17, 0.11]] },
  { id: 'bell', label: 'Bell', type: 'sine', notes: [[1319, 0, 0.55], [1760, 0.005, 0.5]] },
  { id: 'none', label: 'Off', notes: [] },
];

export const DEFAULT_KNOCK_SOUND = 'ding';

let ctx: AudioContext | null = null;
function audioCtx(): AudioContext | null {
  try {
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    ctx = ctx ?? new Ctor();
    if (ctx.state === 'suspended') void ctx.resume();
    return ctx;
  } catch {
    return null;
  }
}

// Browsers refuse to start audio outside a user gesture — and an incoming call
// arrives over a WebSocket, where there is no gesture in sight. A ring started
// on a suspended context is silently dropped, which is exactly how the incoming
// ringtone went missing. So we unlock ONCE, on the first interaction of the
// session: by the time someone rings you, the context is already running.
let unlockArmed = false;

export function unlockAudio(): void {
  if (unlockArmed || typeof window === 'undefined') return;
  unlockArmed = true;

  const unlock = () => {
    const ac = audioCtx();
    if (!ac) return teardown();
    void ac.resume().catch(() => undefined);
    // iOS wants an actual sound played inside the gesture, not just resume():
    // one silent sample is enough to mark the context as user-activated.
    try {
      const src = ac.createBufferSource();
      src.buffer = ac.createBuffer(1, 1, 22050);
      src.connect(ac.destination);
      src.start(0);
    } catch {
      /* nothing to do — the resume above is the part that matters */
    }
    teardown();
  };
  const teardown = () => {
    window.removeEventListener('pointerdown', unlock, true);
    window.removeEventListener('keydown', unlock, true);
    window.removeEventListener('touchstart', unlock, true);
  };

  window.addEventListener('pointerdown', unlock, true);
  window.addEventListener('keydown', unlock, true);
  window.addEventListener('touchstart', unlock, true);
}

/** Play a knock sound by id (a no-op for 'none' / unknown / no Web Audio). */
export function playKnockSound(id: string): void {
  const sound = KNOCK_SOUNDS.find((s) => s.id === id);
  if (!sound || sound.notes.length === 0) return;
  const ac = audioCtx();
  if (!ac) return;
  const now = ac.currentTime;
  const master = ac.createGain();
  master.gain.value = 0.16;
  master.connect(ac.destination);
  for (const [freq, start, dur] of sound.notes) {
    const osc = ac.createOscillator();
    osc.type = sound.type ?? 'sine';
    osc.frequency.value = freq;
    const g = ac.createGain();
    // Fast attack, exponential decay — a pleasant chime, not a click.
    g.gain.setValueAtTime(0.0001, now + start);
    g.gain.exponentialRampToValueAtTime(1, now + start + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
    osc.connect(g);
    g.connect(master);
    osc.start(now + start);
    osc.stop(now + start + dur + 0.03);
  }
}

/** A short "time's up" chime for the shared timer — an ascending triad.
 * Synthesized, no assets (§4.2). No-op where Web Audio is unavailable. */
export function playTimerDone(): void {
  const ac = audioCtx();
  if (!ac) return;
  const now = ac.currentTime;
  const master = ac.createGain();
  master.gain.value = 0.18;
  master.connect(ac.destination);
  const notes: [number, number, number][] = [
    [587, 0, 0.18], // D5
    [740, 0.14, 0.18], // F#5
    [880, 0.28, 0.6], // A5
  ];
  for (const [freq, start, dur] of notes) {
    const osc = ac.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = freq;
    const g = ac.createGain();
    g.gain.setValueAtTime(0.0001, now + start);
    g.gain.exponentialRampToValueAtTime(1, now + start + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
    osc.connect(g);
    g.connect(master);
    osc.start(now + start);
    osc.stop(now + start + dur + 0.03);
  }
}

// Bundled sounds are decoded once and cached (same-origin assets, §4.2).
const soundBuffers = new Map<string, AudioBuffer>();
const soundPending = new Map<string, Promise<AudioBuffer | null>>();
function loadSound(ac: AudioContext, url: string): Promise<AudioBuffer | null> {
  const cached = soundBuffers.get(url);
  if (cached) return Promise.resolve(cached);
  let pending = soundPending.get(url);
  if (!pending) {
    pending = fetch(url)
      .then((r) => r.arrayBuffer())
      .then((data) => ac.decodeAudioData(data))
      .then((buf) => {
        soundBuffers.set(url, buf);
        return buf;
      })
      .catch(() => null);
    soundPending.set(url, pending);
  }
  return pending;
}

/** Branded chime when your instant meeting opens (bundled MP3, served
 * same-origin — no CDN/egress, §4.2). No-op where Web Audio is unavailable or
 * blocked before a user gesture. */
export function playMeetingStart(): void {
  const ac = audioCtx();
  if (!ac) return;
  void loadSound(ac, meetingStartUrl).then((buffer) => {
    if (!buffer) return;
    const src = ac.createBufferSource();
    src.buffer = buffer;
    const g = ac.createGain();
    g.gain.value = 0.7; // controlled volume
    src.connect(g);
    g.connect(ac.destination);
    src.start();
  });
}

// ── call ringing (Layer B3) ────────────────────────────────────────────────
// Both are synthesized loops (no assets, §4.2) with an explicit stop, so a
// resolved call always silences: every caller keeps the returned function and
// invokes it on accept/decline/cancel/timeout.

/** Play one short pattern of notes. Shared by the ring loops. */
function playPattern(notes: [number, number, number][], type: OscillatorType, gain: number): void {
  const ac = audioCtx();
  if (!ac) return;
  const now = ac.currentTime;
  const master = ac.createGain();
  master.gain.value = gain;
  master.connect(ac.destination);
  for (const [freq, start, dur] of notes) {
    const osc = ac.createOscillator();
    osc.type = type;
    osc.frequency.value = freq;
    const g = ac.createGain();
    g.gain.setValueAtTime(0.0001, now + start);
    g.gain.exponentialRampToValueAtTime(1, now + start + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
    osc.connect(g);
    g.connect(master);
    osc.start(now + start);
    osc.stop(now + start + dur + 0.03);
  }
}

/** Repeat `play` every `everyMs` until the returned stop function is called. */
function loop(play: () => void, everyMs: number): () => void {
  play();
  const id = window.setInterval(play, everyMs);
  return () => window.clearInterval(id);
}

/** Silence between one ring and the next, like a phone. */
const RING_GAP_MS = 3000;

/** The incoming-call ringtones an admin can choose between (Settings → Audio).
 * `id` is the contract with the server, which stores the id and never a path —
 * the audio is bundled and content-hashed by the build. */
export interface Ringtone {
  id: string;
  label: string;
  url: string;
}

export const RINGTONES: Ringtone[] = [
  { id: 'brizina-default', label: 'Brizina default', url: ringtoneBrizinaDefaultUrl },
  { id: 'quiet-connection', label: 'Quiet connection', url: ringtoneQuietUrl },
  { id: 'quiet-connection-algiers', label: 'Quiet connection — Algiers', url: ringtoneQuietAlgiersUrl },
  { id: 'warm-connection', label: 'Warm connection', url: ringtoneWarmUrl },
];

export const DEFAULT_RINGTONE = 'brizina-default';

/** Resolve an id to its asset, falling back to the default for an unknown id —
 * an org that picked a ringtone we later renamed must still ring. */
function ringtoneUrl(id: string): string {
  return (RINGTONES.find((r) => r.id === id) ?? RINGTONES[0]!).url;
}

/** The preview in flight, if any — only one tone ever sounds in the picker. */
let previewStop: (() => void) | null = null;

/** Silence the preview in flight, if any. */
function stopRingtonePreview(): void {
  previewStop?.();
}

/** Play a ringtone ONCE — the admin picker's preview. Returns a stop function,
 * and calls `onEnd` when the tone finishes OR is stopped, so a play/stop button
 * can flip itself back without polling. Starting a preview silences the one
 * before it. */
export function previewRingtone(id: string, onEnd?: () => void): () => void {
  stopRingtonePreview();
  let done = false;
  let node: AudioBufferSourceNode | null = null;
  const end = () => {
    if (done) return;
    done = true;
    if (previewStop === stop) previewStop = null;
    onEnd?.();
  };
  const stop = () => {
    try {
      node?.stop();
    } catch {
      /* already finished */
    }
    end();
  };
  previewStop = stop;

  const ac = audioCtx();
  if (!ac) {
    end(); // no Web Audio: report "not playing" rather than leave a stuck button
    return stop;
  }
  void loadSound(ac, ringtoneUrl(id)).then((buffer) => {
    if (done) return;
    if (!buffer) return end();
    const src = ac.createBufferSource();
    src.buffer = buffer;
    const g = ac.createGain();
    g.gain.value = 0.4;
    src.connect(g);
    g.connect(ac.destination);
    src.onended = end;
    src.start();
    node = src;
  });
  return stop;
}

/** The ring the CALLEE hears — the official Brizina ringtone (bundled MP3),
 * repeated with a gap rather than looped back-to-back. Falls back to a
 * synthesized double ring if decode fails, because a silent incoming call is
 * the worse failure. Stop is explicit and idempotent. */
export function startRingtone(id: string = DEFAULT_RINGTONE): () => void {
  const ac = audioCtx();
  if (!ac) return () => {};
  let stopped = false;
  let src: AudioBufferSourceNode | null = null;
  // Cancels the repeat timer (real ringtone or synth fallback — both repeat).
  let stopRepeat: (() => void) | null = null;
  const begin = async (buffer: AudioBuffer | null) => {
    if (stopped) return;
    // Last-ditch resume: harmless when already running, and it succeeds if a
    // gesture happened between arming the unlock and the call arriving.
    if (ac.state === 'suspended') {
      try {
        await ac.resume();
      } catch {
        /* still blocked — warn below */
      }
    }
    if (stopped) return;
    if (ac.state !== 'running') {
      console.warn('[brizina] ringtone blocked: audio is not unlocked yet');
    }
    if (!buffer) {
      stopRepeat = loop(
        () =>
          playPattern(
            [
              [880, 0, 0.32],
              [1046.5, 0.36, 0.32],
            ],
            'sine',
            0.16,
          ),
        2400,
      );
      return;
    }
    // One ring per cycle (NOT src.loop, which repeats back-to-back): a fresh
    // source each time, spaced by the tone's own length plus the gap.
    const ringOnce = () => {
      if (stopped || !buffer) return;
      const node = ac.createBufferSource();
      node.buffer = buffer;
      const g = ac.createGain();
      g.gain.value = 0.4;
      node.connect(g);
      g.connect(ac.destination);
      node.start();
      src = node; // tracked so stop() can silence a ring already sounding
    };
    stopRepeat = loop(ringOnce, buffer.duration * 1000 + RING_GAP_MS);
  };
  void loadSound(ac, ringtoneUrl(id)).then(begin);
  return () => {
    stopped = true;
    // Both halves matter: clear the repeat timer AND cut the ring that is
    // sounding right now. A ringtone outliving its call is the worst bug here.
    stopRepeat?.();
    try {
      src?.stop();
    } catch {
      /* already finished */
    }
  };
}

/** The ringback the CALLER hears while waiting — quieter, single low tone. */
export function startRingback(): () => void {
  return loop(() => playPattern([[440, 0, 0.5]], 'sine', 0.07), 2400);
}
